import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import {
  TextField,
  Button,
  Box,
  Stack,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  Typography,
  IconButton,
  Alert,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material'
import { Link as RouterLink } from 'react-router-dom'
import type { UserSettings, UserSettingsUpdate, ThresholdItem } from '@/types'
import type { StudentSettingsRestrictionReason } from '@/api/userSettings'
import { ROUTES } from '@/utils/constants'


const thresholdItemSchema = (t: (key: string) => string) =>
  z.object({
    name: z
      .string()
      .min(1, t('profile.settings.validation.nameRequired'))
      .max(50, t('profile.settings.validation.nameMaxLength')),
    from: z.number().min(0).max(100),
    to: z.number().min(0).max(100),
    count_cards_in_test: z.number().min(1).max(100),
    regression_interval_in_days: z
      .number()
      .min(1, t('profile.settings.validation.regressionDaysMin')),
  }).refine((data) => data.to > data.from, {
    message: t('profile.settings.validation.fromToOrder'),
    path: ['to'],
  })

function createSettingsSchema(t: (key: string) => string) {
  return z
    .object({
      max_progress_card: z
        .number()
        .min(1, t('profile.settings.validation.maxProgressRange'))
        .max(100, t('profile.settings.validation.maxProgressRange')),
      count_cards_in_test: z
        .number()
        .min(3, t('profile.settings.validation.countCardsRange'))
        .max(100, t('profile.settings.validation.countCardsRange')),
      card_regression: z.boolean(),
      card_regression_regardless_of_tests: z.boolean(),
      use_default_settings: z.boolean(),
      thresholds: z.array(thresholdItemSchema(t)),
    })
    .refine(
      (data) => {
        if (data.use_default_settings || !data.thresholds?.length) return true
        const sum = data.thresholds.reduce((s, t) => s + t.count_cards_in_test, 0)
        return sum === 100
      },
      { message: t('profile.settings.validation.thresholdsSumMustBe100'), path: ['thresholds'] }
    )
    .refine(
      (data) => {
        if (data.use_default_settings || !data.thresholds?.length) return true
        const sorted = [...data.thresholds].sort((a, b) => a.from - b.from)
        const covered: number[] = []
        const repeated: number[] = []
        for (const interval of sorted) {
          for (let i = interval.from; i <= interval.to; i++) {
            if (covered.includes(i)) repeated.push(i)
            covered.push(i)
          }
        }
        if (repeated.length > 0) return false
        covered.sort((a, b) => a - b)
        let start = 0
        for (const n of covered) {
          if (n > start) return false
          start = n + 1
        }
        return start > 100
      },
      { message: t('profile.settings.validation.thresholdsIntervalsOverlap'), path: ['thresholds'] }
    )
}

export type SettingsFormValues = z.infer<ReturnType<typeof createSettingsSchema>>

function thresholdsToItems(thresholds: UserSettings['thresholds']): ThresholdItem[] {
  if (!thresholds?.length) return []
  return thresholds.map((t) => ({
    name: t.name,
    from: t.from,
    to: t.to ?? 100,
    count_cards_in_test: t.count_cards_in_test,
    regression_interval_in_days: t.regression_interval_in_days,
  }))
}

interface SettingsFormProps {
  initialData: UserSettings
  onSubmit: (data: UserSettingsUpdate) => Promise<void>
  isLoading?: boolean
  /** Причина ограничения редактирования индивидуальных настроек ученика (только для страницы настроек ученика) */
  studentSettingsRestriction?: StudentSettingsRestrictionReason | null
}

export function SettingsForm({
  initialData,
  onSubmit,
  isLoading = false,
  studentSettingsRestriction = null,
}: SettingsFormProps) {
  const isRestricted = Boolean(studentSettingsRestriction)
  const { t } = useTranslation()
  const schema = createSettingsSchema(t)

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
    trigger,
  } = useForm<SettingsFormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      max_progress_card: initialData.max_progress_card ?? 10,
      count_cards_in_test: initialData.count_cards_in_test ?? 10,
      card_regression: initialData.card_regression ?? false,
      card_regression_regardless_of_tests: initialData.card_regression_regardless_of_tests ?? false,
      use_default_settings: isRestricted ? true : (initialData.use_default_settings ?? false),
      thresholds: thresholdsToItems(initialData.thresholds),
    },
  })

  const useDefault = watch('use_default_settings')
  const { fields, append, remove } = useFieldArray({ control, name: 'thresholds' })

  const onFormSubmit = async (data: SettingsFormValues) => {
    if (data.use_default_settings) {
      await onSubmit({ use_default_settings: true })
      return
    }
    const payload: UserSettingsUpdate = {
      max_progress_card: data.max_progress_card,
      count_cards_in_test: data.count_cards_in_test,
      card_regression: data.card_regression,
      card_regression_regardless_of_tests: data.card_regression_regardless_of_tests,
    }
    if (data.thresholds?.length) {
      payload.thresholds = data.thresholds
    }
    await onSubmit(payload)
  }

  const addThreshold = () => {
    const from = fields.length === 0 ? 0 : (fields[fields.length - 1] as { to: number }).to + 1
    append({
      name: '',
      from,
      to: Math.min(from + 25, 100),
      count_cards_in_test: 25,
      regression_interval_in_days: 10,
    })
  }

  const restrictionMessage =
    studentSettingsRestriction === 'subscription_expired'
      ? t('profile.settings.studentRestriction.subscriptionExpired')
      : studentSettingsRestriction === 'not_in_plan'
        ? t('profile.settings.studentRestriction.notInPlan')
        : ''
  const restrictionLink =
    studentSettingsRestriction === 'subscription_expired'
      ? ROUTES.PROFILE_SUBSCRIPTION
      : studentSettingsRestriction === 'not_in_plan'
        ? ROUTES.PROFILE_PLANS
        : ''
  const restrictionLinkText =
    studentSettingsRestriction === 'subscription_expired'
      ? t('profile.settings.studentRestriction.subscriptionExpiredLink')
      : studentSettingsRestriction === 'not_in_plan'
        ? t('profile.settings.studentRestriction.notInPlanLink')
        : ''

  return (
    <form onSubmit={handleSubmit(onFormSubmit)}>
      <Stack spacing={3}>
        {isRestricted && (
          <Alert severity="info">
            {restrictionMessage}
            {restrictionLink && (
              <Link component={RouterLink} to={restrictionLink} sx={{ ml: 1 }}>
                {restrictionLinkText}
              </Link>
            )}
          </Alert>
        )}

        <Card>
          <CardContent>
            <Controller
              name="use_default_settings"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(_, v) => field.onChange(v)}
                      color="primary"
                      disabled={isRestricted}
                    />
                  }
                  label={t('profile.settings.useDefaultSettings')}
                />
              )}
            />
          </CardContent>
        </Card>

        {(useDefault || isRestricted) && (
          <Card variant="outlined" sx={{ bgcolor: 'var(--color-disable-surface)' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('profile.settings.defaultsPreviewTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('profile.settings.defaultsPreviewDescription')}
              </Typography>
              <Stack spacing={1.5}>
                <Stack spacing={0.75}>
                  <Typography variant="body2">
                    <strong>{t('profile.settings.maxProgressCard')}:</strong>{' '}
                    {initialData.max_progress_card ?? 10}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t('profile.settings.countCardsInTest')}:</strong>{' '}
                    {initialData.count_cards_in_test ?? 10}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t('profile.settings.cardRegression')}:</strong>{' '}
                    {initialData.card_regression ? t('common.yes') : t('common.no')}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t('profile.settings.cardRegressionRegardlessOfTests')}:</strong>{' '}
                    {initialData.card_regression_regardless_of_tests ? t('common.yes') : t('common.no')}
                  </Typography>
                </Stack>
                {initialData.thresholds?.length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>
                      {t('profile.settings.defaultsThresholdsTitle')}
                    </Typography>
                    <TableContainer sx={{ width: '100%' }}>
                      <Table size="small" sx={{ width: '100%' }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ p: 0.75, fontWeight: 700 }}>
                              {t('profile.settings.thresholdName')}
                            </TableCell>
                            <TableCell sx={{ p: 0.75, fontWeight: 700, whiteSpace: 'nowrap' }}>
                              {t('profile.settings.thresholdInterval')}
                            </TableCell>
                            <TableCell sx={{ p: 0.75, fontWeight: 700, whiteSpace: 'nowrap' }}>
                              {t('profile.settings.thresholdCountCards')}
                            </TableCell>
                            <TableCell sx={{ p: 0.75, fontWeight: 700, whiteSpace: 'nowrap' }}>
                              {t('profile.settings.thresholdRegressionDays')}
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {thresholdsToItems(initialData.thresholds).map((th, idx) => (
                            <TableRow key={idx}>
                              <TableCell sx={{ p: 0.75, verticalAlign: 'top' }}>
                                {th.name}
                              </TableCell>
                              <TableCell sx={{ p: 0.75, verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                                {th.from}–{th.to ?? 100}%
                              </TableCell>
                              <TableCell sx={{ p: 0.75, verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                                {th.count_cards_in_test}
                              </TableCell>
                              <TableCell sx={{ p: 0.75, verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                                {th.regression_interval_in_days}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>
        )}

        {!useDefault && !isRestricted && (
          <>
            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Controller
                    name="max_progress_card"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="number"
                        label={t('profile.settings.maxProgressCard')}
                        helperText={errors.max_progress_card?.message || t('profile.settings.maxProgressCardHelp')}
                        error={!!errors.max_progress_card}
                        onChange={(e) => {
                          field.onChange(parseInt(e.target.value, 10) || 0)
                          trigger('max_progress_card')
                        }}
                        inputProps={{ min: 1, max: 100 }}
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="count_cards_in_test"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="number"
                        label={t('profile.settings.countCardsInTest')}
                        helperText={errors.count_cards_in_test?.message || t('profile.settings.countCardsInTestHelp')}
                        error={!!errors.count_cards_in_test}
                        onChange={(e) => {
                          field.onChange(parseInt(e.target.value, 10) || 0)
                          trigger('count_cards_in_test')
                        }}
                        inputProps={{ min: 3, max: 100 }}
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="card_regression"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            onChange={(_, v) => field.onChange(v)}
                            color="primary"
                          />
                        }
                        label={t('profile.settings.cardRegression')}
                      />
                    )}
                  />
                  <Controller
                    name="card_regression_regardless_of_tests"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            onChange={(_, v) => field.onChange(v)}
                            color="primary"
                          />
                        }
                        label={t('profile.settings.cardRegressionRegardlessOfTests')}
                      />
                    )}
                  />
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">{t('profile.settings.thresholds')}</Typography>
                  <Button
                    type="button"
                    startIcon={<AddIcon />}
                    onClick={addThreshold}
                    size="small"
                    variant="outlined"
                  >
                    {t('profile.settings.addThreshold')}
                  </Button>
                </Box>
                {(errors.thresholds?.message || errors.thresholds?.root?.message) && (
                  <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                    {errors.thresholds.message || errors.thresholds.root?.message}
                  </Typography>
                )}
                <Stack spacing={2}>
                  {fields.map((field, index) => (
                    <Box
                      key={field.id}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr 1fr 1fr auto' },
                        gap: 2,
                        alignItems: 'flex-start',
                      }}
                    >
                      <Controller
                        name={`thresholds.${index}.name`}
                        control={control}
                        render={({ field: f }) => (
                          <TextField
                            {...f}
                            label={t('profile.settings.thresholdName')}
                            size="small"
                            error={!!errors.thresholds?.[index]?.name}
                            helperText={errors.thresholds?.[index]?.name?.message}
                            fullWidth
                          />
                        )}
                      />
                      <Controller
                        name={`thresholds.${index}.from`}
                        control={control}
                        render={({ field: f }) => (
                          <TextField
                            {...f}
                            type="number"
                            label={t('profile.settings.thresholdFrom')}
                            size="small"
                            error={!!errors.thresholds?.[index]?.from}
                            helperText={errors.thresholds?.[index]?.from?.message}
                            onChange={(e) => {
                              f.onChange(parseInt(e.target.value, 10) || 0)
                              trigger(`thresholds.${index}.from`)
                              trigger(`thresholds.${index}.to`)
                              trigger('thresholds')
                            }}
                            inputProps={{ min: 0, max: 100 }}
                            fullWidth
                          />
                        )}
                      />
                      <Controller
                        name={`thresholds.${index}.to`}
                        control={control}
                        render={({ field: f }) => (
                          <TextField
                            {...f}
                            type="number"
                            label={t('profile.settings.thresholdTo')}
                            size="small"
                            error={!!errors.thresholds?.[index]?.to}
                            helperText={errors.thresholds?.[index]?.to?.message}
                            onChange={(e) => {
                              f.onChange(parseInt(e.target.value, 10) || 0)
                              trigger(`thresholds.${index}.to`)
                              trigger(`thresholds.${index}.from`)
                              trigger('thresholds')
                            }}
                            inputProps={{ min: 0, max: 100 }}
                            fullWidth
                          />
                        )}
                      />
                      <Controller
                        name={`thresholds.${index}.count_cards_in_test`}
                        control={control}
                        render={({ field: f }) => (
                          <TextField
                            {...f}
                            type="number"
                            label={t('profile.settings.thresholdCountCards')}
                            size="small"
                            error={!!errors.thresholds?.[index]?.count_cards_in_test}
                            helperText={errors.thresholds?.[index]?.count_cards_in_test?.message}
                            onChange={(e) => {
                              f.onChange(parseInt(e.target.value, 10) || 0)
                              trigger(`thresholds.${index}.count_cards_in_test`)
                              trigger('thresholds')
                            }}
                            inputProps={{ min: 1, max: 100 }}
                            fullWidth
                          />
                        )}
                      />
                      <Controller
                        name={`thresholds.${index}.regression_interval_in_days`}
                        control={control}
                        render={({ field: f }) => (
                          <TextField
                            {...f}
                            type="number"
                            label={t('profile.settings.thresholdRegressionDays')}
                            size="small"
                            error={!!errors.thresholds?.[index]?.regression_interval_in_days}
                            helperText={errors.thresholds?.[index]?.regression_interval_in_days?.message}
                            onChange={(e) => {
                              f.onChange(parseInt(e.target.value, 10) || 1)
                              trigger(`thresholds.${index}.regression_interval_in_days`)
                            }}
                            inputProps={{ min: 1 }}
                            fullWidth
                          />
                        )}
                      />
                      <IconButton
                        type="button"
                        onClick={() => remove(index)}
                        color="error"
                        aria-label={t('profile.settings.removeThreshold')}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </>
        )}

        {Object.keys(errors).length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('profile.settings.validation.formHasErrors')}
            {(errors.thresholds?.message || errors.thresholds?.root?.message) && (
              <Box component="div" sx={{ mt: 1 }}>
                {errors.thresholds.message || errors.thresholds.root?.message}
              </Box>
            )}
          </Alert>
        )}

        {!isRestricted && (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading || isSubmitting}
            >
              {isLoading || isSubmitting ? t('common.loading') : t('profile.settings.save')}
            </Button>
          </Box>
        )}
      </Stack>
    </form>
  )
}
